import {
  CornerDownRight,
  FileText,
  ImageIcon,
  MessageCircle,
  Paperclip,
  Send,
  X,
} from "lucide-react";
import { type ChangeEvent, type FormEvent, type ReactNode, useMemo, useRef, useState } from "react";

import type { QuoteCommentAttachmentResponse, QuoteCommentResponse } from "@/api/types";
import { formatTime } from "@/lib/format";

/** Allowed MIME types and per-file caps mirror the backend (see
 *  ``packages/api/pencilout/api/services/attachments.py``). Validation
 *  here is a UX shortcut — the server still rejects anything it
 *  doesn't trust with a 400. */
const ALLOWED_IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/heic", "image/webp"]);
const ALLOWED_PDF_MIME_TYPE = "application/pdf";
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_PDF_BYTES = 20 * 1024 * 1024;
const MAX_ATTACHMENTS_PER_COMMENT = 10;
const FILE_INPUT_ACCEPT = "image/jpeg,image/png,image/heic,image/webp,application/pdf";

export interface CommentThreadProps {
  comments: QuoteCommentResponse[];
  loading?: boolean;
  /** Called when the user submits a new comment.
   *  - Public view: ``name`` is required, ``parentId`` optional.
   *    The backend ignores the typed name (uses linked client) but
   *    the form still surfaces it as a read-only display.
   *  - Contractor view: ``name`` is undefined, ``parentId`` may
   *    point at the comment being replied to.
   *  - When ``files`` is non-empty, the call site posts as
   *    ``multipart/form-data``; otherwise the existing JSON path
   *    is used. */
  onSubmit: (input: {
    name?: string;
    body: string;
    parentId?: string | null;
    files?: File[];
  }) => Promise<void>;
  errorMessage?: string | null;
  submitting?: boolean;
  /** Read-only display name shown above the compose field on the
   * public view (so the homeowner sees who they're posting as).
   * Server uses the linked client's name regardless of what's
   * here; this is purely informational. */
  identityName?: string;
  /** Whether the public view should request a name field. v1 still
   * accepts this prop for api back-compat but never asks the user
   * to type — server-side derives identity from the share token. */
  requireName?: boolean;
  composeLabel?: string;
  composePlaceholder?: string;
  /** When true, every comment gets a "Reply" affordance so the
   * contractor can target a specific client message. Public view
   * keeps replies flat for now (clients only have one identity). */
  showReplyButton?: boolean;
  /** API base used to resolve relative ``download_url`` paths on
   * comment attachments into absolute URLs the browser can fetch.
   * Required for any view that renders attachments. */
  apiBase?: string;
  /** Enable the paperclip button + file picker on the compose form.
   * Off by default — both the public homeowner view AND the
   * contractor view turn this on (homeowner posts photos of their
   * walls, contractor sends back a marked-up estimate snapshot). */
  allowAttachments?: boolean;
  /** Optional renderer for an action below each rendered attachment.
   * The contractor surface uses this to mount a "Send to job" button
   * per attachment. Public-quote view leaves it undefined (homeowners
   * have no concept of curated job attachments). */
  renderAttachmentAction?: (input: {
    comment: QuoteCommentResponse;
    attachment: QuoteCommentAttachmentResponse;
  }) => ReactNode;
}

/** ``2.3 MB`` / ``842 KB`` / ``512 B``. Same rounding as the contractor
 *  side; keeps the chip and the rendered metadata visually identical. */
function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/** Validate one File against the same allow-list + caps as the API.
 *  Returns a human-readable reason on rejection or ``null`` on accept. */
function validateAttachment(file: File): string | null {
  const mime = file.type.toLowerCase();
  const isImage = ALLOWED_IMAGE_MIME_TYPES.has(mime);
  const isPdf = mime === ALLOWED_PDF_MIME_TYPE;
  if (!isImage && !isPdf) {
    return `${file.name}: unsupported file type. Use JPG, PNG, HEIC, WEBP, or PDF.`;
  }
  if (isImage && file.size > MAX_IMAGE_BYTES) {
    return `${file.name}: image is too large (max ${MAX_IMAGE_BYTES / (1024 * 1024)} MB).`;
  }
  if (isPdf && file.size > MAX_PDF_BYTES) {
    return `${file.name}: PDF is too large (max ${MAX_PDF_BYTES / (1024 * 1024)} MB).`;
  }
  return null;
}

export function CommentThread({
  comments,
  loading,
  onSubmit,
  errorMessage,
  submitting,
  identityName,
  composeLabel = "Add a comment",
  composePlaceholder = "Type your message…",
  showReplyButton = false,
  apiBase,
  allowAttachments = false,
  renderAttachmentAction,
}: CommentThreadProps) {
  const [body, setBody] = useState("");
  const [replyingTo, setReplyingTo] = useState<QuoteCommentResponse | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Group comments by their parent (or self if top-level) so we can
  // render replies nested under their parent. One level deep — the
  // server collapses deeper attempts to the same root anchor.
  //
  // Latest-first order: top-level comments are reversed so the most
  // recent message sits closest to the composer at the top of the
  // panel. Replies under each parent stay chronological because the
  // homeowner reads a reply chain top-to-bottom.
  const tree = useMemo(() => {
    const replies = new Map<string, QuoteCommentResponse[]>();
    const top: QuoteCommentResponse[] = [];
    for (const c of comments) {
      if (c.parent_comment_id) {
        const arr = replies.get(c.parent_comment_id) ?? [];
        arr.push(c);
        replies.set(c.parent_comment_id, arr);
      } else {
        top.push(c);
      }
    }
    // Reverse top-level so latest renders first (under the composer).
    top.reverse();
    return { top, replies };
  }, [comments]);

  // Collapse threads longer than this when first rendered. The
  // contractor opens the panel, sees the most recent few messages
  // + the composer, and clicks "Show N older" if they need history.
  const COLLAPSE_THRESHOLD = 5;
  const [showAll, setShowAll] = useState(false);
  const visibleTop =
    showAll || tree.top.length <= COLLAPSE_THRESHOLD
      ? tree.top
      : tree.top.slice(0, COLLAPSE_THRESHOLD);
  const hiddenCount = Math.max(0, tree.top.length - visibleTop.length);

  function handleFilesPicked(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    // Reset native input value so picking the same file twice in a
    // row still re-triggers ``change``. Without this, removing then
    // re-adding the same image is silently no-op.
    e.target.value = "";
    if (picked.length === 0) return;

    const errors: string[] = [];
    const accepted: File[] = [];
    for (const file of picked) {
      const reason = validateAttachment(file);
      if (reason) {
        errors.push(reason);
      } else {
        accepted.push(file);
      }
    }

    setPendingFiles((prev) => {
      const next = [...prev, ...accepted];
      if (next.length > MAX_ATTACHMENTS_PER_COMMENT) {
        errors.push(`Maximum ${MAX_ATTACHMENTS_PER_COMMENT} attachments per comment.`);
        return next.slice(0, MAX_ATTACHMENTS_PER_COMMENT);
      }
      return next;
    });
    setFileError(errors.length > 0 ? errors.join(" ") : null);
  }

  function removeFileAt(idx: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
    setFileError(null);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!body.trim()) return;
    await onSubmit({
      body: body.trim(),
      parentId: replyingTo?.id ?? null,
      files: pendingFiles.length > 0 ? pendingFiles : undefined,
    });
    setBody("");
    setReplyingTo(null);
    setPendingFiles([]);
    setFileError(null);
  }

  const canSubmit = body.trim().length > 0;

  return (
    <section className="rounded-lg border border-rule bg-paper-warm shadow-sm">
      {/* Sticky header — visually anchors the conversation as a
          distinct sidebar surface. Compact: the composer below is
          where the contractor's attention goes. */}
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-lg border-b border-rule bg-paper-warm px-3 py-2">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-graphite-soft" aria-hidden />
          <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Conversation
            {comments.length > 0 && (
              <span className="ml-1 normal-case text-graphite-faint">
                ({comments.length})
              </span>
            )}
          </h3>
        </div>
      </header>

      <div className="space-y-3 p-3">
        {/* Composer at top: the contractor's reply is the first thing
            they see + can act on without scrolling past history. */}
        <form onSubmit={submit} className="space-y-2 rounded-lg border border-rule bg-paper p-3">
        <div className="flex items-center justify-between">
          <h4 className="label-mono">
            {replyingTo ? `Reply to ${replyingTo.author_name}` : composeLabel}
          </h4>
          {replyingTo && (
            <button
              type="button"
              onClick={() => setReplyingTo(null)}
              className="text-graphite-soft hover:text-graphite"
              aria-label="Cancel reply"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>

        {replyingTo && (
          <div className="rounded border border-rule bg-paper px-2 py-1.5 text-xs italic text-graphite-soft">
            <CornerDownRight className="mr-1 inline h-3 w-3" aria-hidden />
            Replying to: "{replyingTo.body.slice(0, 100)}
            {replyingTo.body.length > 100 ? "…" : ""}"
          </div>
        )}

        {identityName && (
          <p className="font-mono text-[10px] text-graphite-soft">
            Posting as <span className="text-graphite">{identityName}</span>
          </p>
        )}

        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={composePlaceholder}
          rows={3}
          // text-base so iOS doesn't auto-zoom the page when the
          // textarea gains focus (it auto-zooms when font-size <16px).
          // Bumped on the public homeowner-facing thread; sm:text-sm
          // restores the desktop density.
          className="w-full rounded border border-rule bg-paper px-2 py-1.5 text-base text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none sm:text-sm"
        />

        {allowAttachments && pendingFiles.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {pendingFiles.map((file, idx) => (
              <li
                key={`${file.name}-${idx}-${file.size}`}
                className="inline-flex max-w-full items-center gap-1.5 rounded border border-rule bg-paper px-2 py-1 font-mono text-[11px] text-graphite"
              >
                {file.type === ALLOWED_PDF_MIME_TYPE ? (
                  <FileText className="h-3 w-3 flex-none text-graphite-soft" aria-hidden />
                ) : (
                  <ImageIcon className="h-3 w-3 flex-none text-graphite-soft" aria-hidden />
                )}
                <span className="min-w-0 truncate" title={file.name}>
                  {file.name}
                </span>
                <span className="flex-none text-graphite-soft">{formatBytes(file.size)}</span>
                <button
                  type="button"
                  onClick={() => removeFileAt(idx)}
                  className="ml-0.5 flex-none text-graphite-soft hover:text-red-pencil"
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}

        {fileError && <p className="text-sm text-red-pencil">{fileError}</p>}
        {errorMessage && <p className="text-sm text-red-pencil">{errorMessage}</p>}

        <div className="flex flex-wrap items-center justify-between gap-2">
          {allowAttachments ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={FILE_INPUT_ACCEPT}
                multiple
                className="hidden"
                onChange={handleFilesPicked}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting || pendingFiles.length >= MAX_ATTACHMENTS_PER_COMMENT}
                className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-2.5 py-1.5 font-mono text-xs font-semibold text-graphite-soft transition hover:bg-pencil-yellow-soft hover:text-graphite disabled:opacity-50"
                aria-label="Attach files"
              >
                <Paperclip className="h-3.5 w-3.5" aria-hidden />
                <span className="hidden sm:inline">Attach</span>
              </button>
            </>
          ) : (
            // Spacer so the submit button stays right-aligned.
            <span />
          )}
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
          >
            <Send className="h-3 w-3" aria-hidden />
            {submitting ? "Posting…" : replyingTo ? "Post reply" : "Post"}
          </button>
        </div>
      </form>

      {/* Messages: latest first, immediately under the composer. */}
      {loading && comments.length === 0 && (
        <p className="text-xs text-graphite-soft">Loading thread…</p>
      )}

      {!loading && comments.length === 0 && (
        <p className="rounded border border-rule bg-paper px-3 py-3 text-xs text-graphite-soft">
          No messages yet.
        </p>
      )}

      {comments.length > 0 && (
        <>
          <ul className="space-y-2">
            {visibleTop.map((c) => (
              <CommentBubble
                key={c.id}
                comment={c}
                replies={tree.replies.get(c.id) ?? []}
                showReplyButton={showReplyButton}
                onReply={setReplyingTo}
                apiBase={apiBase}
                renderAttachmentAction={renderAttachmentAction}
              />
            ))}
          </ul>

          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="w-full rounded border border-rule bg-paper px-3 py-2 font-mono text-xs font-semibold text-graphite-soft transition hover:bg-pencil-yellow-soft hover:text-graphite"
            >
              Show {hiddenCount} older message{hiddenCount === 1 ? "" : "s"}
            </button>
          )}
        </>
      )}
      </div>
    </section>
  );
}

function CommentBubble({
  comment,
  replies,
  showReplyButton,
  onReply,
  nested = false,
  apiBase,
  renderAttachmentAction,
}: {
  comment: QuoteCommentResponse;
  replies: QuoteCommentResponse[];
  showReplyButton: boolean;
  /** Called with the comment that should become the reply target.
   * Backend collapses the parent_id to the original top-level
   * anchor regardless, so passing a nested comment here just stages
   * a new sibling reply under the same conversation root. */
  onReply: (target: QuoteCommentResponse) => void;
  nested?: boolean;
  /** Forwarded down so attachments can resolve their relative
   * ``download_url`` into something the browser can fetch. */
  apiBase?: string;
  renderAttachmentAction?: (input: {
    comment: QuoteCommentResponse;
    attachment: QuoteCommentAttachmentResponse;
  }) => ReactNode;
}) {
  const attachments = comment.attachments ?? [];
  const hasBody = comment.body.trim().length > 0;
  return (
    <li className={nested ? "ml-6 border-l border-rule pl-3" : ""}>
      <div
        className={[
          "rounded-lg border p-3",
          comment.author_type === "contractor"
            ? "border-rule bg-paper-warm"
            : "border-pencil-yellow/40 bg-pencil-yellow-soft",
        ].join(" ")}
      >
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-medium text-graphite">{comment.author_name}</span>
          <span
            className={[
              "rounded px-1.5 py-0.5 font-mono text-[10px]",
              comment.author_type === "contractor"
                ? "bg-graphite text-paper"
                : "bg-paper-warm text-graphite",
            ].join(" ")}
          >
            {comment.author_type === "contractor" ? "Contractor" : "Client"}
          </span>
          <span className="font-mono text-[10px] text-graphite-soft">
            {formatTime(comment.created_at)}
          </span>
          {showReplyButton && (
            <button
              type="button"
              onClick={() => onReply(comment)}
              className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] text-graphite-soft transition hover:text-graphite"
            >
              <CornerDownRight className="h-3 w-3" aria-hidden />
              Reply
            </button>
          )}
        </div>
        {hasBody && (
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-graphite">
            {comment.body}
          </p>
        )}
        {attachments.length > 0 && (
          <CommentAttachments
            comment={comment}
            attachments={attachments}
            apiBase={apiBase}
            // Pull the gallery flush against the bubble's top edge
            // when there's no text — keeps an attachment-only comment
            // (homeowner just attaching a photo) visually tight.
            tightTop={!hasBody}
            renderAttachmentAction={renderAttachmentAction}
          />
        )}
      </div>

      {replies.length > 0 && (
        <ul className="mt-2 space-y-2">
          {replies.map((r) => (
            <CommentBubble
              key={r.id}
              comment={r}
              replies={[]}
              showReplyButton={showReplyButton}
              onReply={onReply}
              nested
              apiBase={apiBase}
              renderAttachmentAction={renderAttachmentAction}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/** Render the attachment gallery for one comment.
 *
 *  Images render as 200×200 lazy-loaded thumbnails (tap-to-open in a
 *  new tab). PDFs render as a file-icon row with the original filename
 *  + size pill. Anything we don't recognize falls through to the
 *  PDF-style fallback so a forward-compat MIME (e.g. ``image/avif``)
 *  added server-side still renders something clickable.
 *
 *  When ``renderAttachmentAction`` is provided, each rendered
 *  attachment also gets the returned node hung beneath it — that's
 *  where the contractor surface mounts a "Send to job" button. */
function CommentAttachments({
  comment,
  attachments,
  apiBase,
  tightTop,
  renderAttachmentAction,
}: {
  comment: QuoteCommentResponse;
  attachments: QuoteCommentAttachmentResponse[];
  apiBase: string | undefined;
  tightTop: boolean;
  renderAttachmentAction?: (input: {
    comment: QuoteCommentResponse;
    attachment: QuoteCommentAttachmentResponse;
  }) => ReactNode;
}) {
  function resolve(url: string): string {
    if (!apiBase) return url;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${apiBase}${url}`;
  }

  const images = attachments.filter((a) => a.mime_type.startsWith("image/"));
  const others = attachments.filter((a) => !a.mime_type.startsWith("image/"));

  return (
    <div className={tightTop ? "" : "mt-2"}>
      {images.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {images.map((att) => {
            const href = resolve(att.download_url);
            const action = renderAttachmentAction?.({ comment, attachment: att });
            return (
              <li key={att.id} className="flex flex-col gap-1.5">
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block overflow-hidden rounded border border-rule bg-paper transition hover:border-graphite"
                  aria-label={`Open ${att.original_filename} in a new tab`}
                >
                  <img
                    src={href}
                    alt={att.original_filename}
                    loading="lazy"
                    // Mobile-first: cap at 160px so two thumbs fit side-
                    // by-side on a 360-wide screen. Bumps to 200px on
                    // tablet+ where there's more horizontal room.
                    className="h-40 w-40 object-cover sm:h-[200px] sm:w-[200px]"
                  />
                </a>
                <div className="flex flex-wrap items-center justify-between gap-1.5 text-[10px] text-graphite-soft">
                  <span className="truncate font-mono" title={att.original_filename}>
                    {att.original_filename}
                  </span>
                  <span className="font-mono tabular-nums">{formatBytes(att.size_bytes)}</span>
                </div>
                {action}
              </li>
            );
          })}
        </ul>
      )}

      {others.length > 0 && (
        <ul className={images.length > 0 ? "mt-2 space-y-1.5" : "space-y-1.5"}>
          {others.map((att) => {
            const action = renderAttachmentAction?.({ comment, attachment: att });
            return (
              <li key={att.id} className="space-y-1.5">
                <a
                  href={resolve(att.download_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex max-w-full items-center gap-2 rounded border border-rule bg-paper px-2.5 py-1.5 text-xs text-graphite transition hover:border-graphite hover:bg-pencil-yellow-soft"
                >
                  <FileText className="h-4 w-4 flex-none text-graphite-soft" aria-hidden />
                  <span className="min-w-0 truncate font-medium" title={att.original_filename}>
                    {att.original_filename}
                  </span>
                  <span className="flex-none rounded bg-paper-warm px-1.5 py-0.5 font-mono text-[10px] text-graphite-soft">
                    {formatBytes(att.size_bytes)}
                  </span>
                </a>
                {action}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
